import { domainRedirectMap } from "../../root-loader/apply-hostname-redirect";

const domainBySiteSlug = Object.fromEntries(
  Object.entries(domainRedirectMap).map(([domain, siteSlug]) => [
    siteSlug,
    domain,
  ]),
);

/**
 * Grab the market slug from the path.
 * This handles both regular URLs like `/se/...` and sitemap URLs like `/sitemap/links/se`.
 */
const getSiteSlug = (pathname: string): string | undefined => {
  const segments = pathname.toLowerCase().split("/").filter(Boolean);

  return segments[0] === "sitemap" ? segments[2] : segments[0];
};

/**
 * Take a full URL and swap just the domain when the path tells us
 * which market the URL belongs to.
 */
export const rewriteUrlDomainBySiteSlug = (value: string): string => {
  try {
    const url = new URL(value);
    const siteSlug = getSiteSlug(url.pathname);
    const domain = siteSlug ? domainBySiteSlug[siteSlug] : undefined;
    url.hostname = domain ? `www.${domain}` : "www.missmary.com";
    return url.toString();
  } catch {
    return value;
  }
};

/**
 * Run the same domain swap over every `<loc>` entry in the sitemap XML.
 */
export const rewriteSitemapLocDomains = (xml: string): string =>
  xml.replace(/<loc>([^<]+)<\/loc>/g, (_match, loc: string) => {
    return `<loc>${rewriteUrlDomainBySiteSlug(loc)}</loc>`;
  });

/**
 * Read the upstream sitemap response, rewrite the XML body,
 * then return a fresh response with the same status and headers.
 */
export const rewriteSitemapResponse = async (
  response: Response,
): Promise<Response> => {
  const contentType = response.headers.get("content-type") ?? "";

  if (!contentType.includes("xml")) {
    return response;
  }

  const xml = await response.text();
  const headers = new Headers(response.headers);

  headers.delete("content-length");

  return new Response(rewriteSitemapLocDomains(xml), {
    headers,
    status: response.status,
    statusText: response.statusText,
  });
};
